import { DateTime } from 'luxon';

export type FormatOptions = {
    timezone: string;
    use24HourClock: boolean;
    /**
     * BCP-47 tag the dates are rendered in — month names, digit order and the
     * am/pm marker all come from it. Supplied by `useFormatDate` from the
     * active i18next language, so a date follows the language the user chose.
     */
    locale: string;
};

/**
 * What `formatSmartShortTimeAndDate` worked out. It returns the parts rather
 * than a sentence so the words and their order stay in the translation layer —
 * a pure function has no business deciding that "Today" comes first.
 */
export type SmartShortTimeAndDate =
    | { kind: 'today'; time: string }
    | { kind: 'yesterday'; time: string }
    | { kind: 'date'; time: string; date: string };

/**
 * Returns the start of day (00:00:00.000) for a given date in the specified timezone, as a UTC ISO string.
 */
export function getStartOfDayInTimezone(date: Date, timezone: string): string {
    const iso = DateTime.fromJSDate(date)
        .setZone(timezone)
        .startOf('day')
        .toUTC()
        .toISO();
    return iso ?? date.toISOString();
}

/**
 * Returns the end of day (23:59:59.999) for a given date in the specified timezone, as a UTC ISO string.
 */
export function getEndOfDayInTimezone(date: Date, timezone: string): string {
    const iso = DateTime.fromJSDate(date)
        .setZone(timezone)
        .endOf('day')
        .toUTC()
        .toISO();
    return iso ?? date.toISOString();
}

export function formatTime(date: Date, opts: FormatOptions): string {
    return new Intl.DateTimeFormat(opts.locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: !opts.use24HourClock,
        timeZone: opts.timezone
    }).format(date);
}

export function formatDateOnly(date: Date, opts: FormatOptions): string {
    return new Intl.DateTimeFormat(opts.locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: opts.timezone
    }).format(date);
}

export function formatDateTime(date: Date, opts: FormatOptions): string {
    const time = formatTime(date, opts);
    const dateStr = formatDateOnly(date, opts);
    return `${time} ${dateStr}`;
}

export function formatTimeAndDate(
    date: Date,
    opts: FormatOptions
): { time: string; date: string } {
    return {
        time: formatTime(date, opts),
        date: formatDateOnly(date, opts)
    };
}

function getDatePartsInTimezone(
    date: Date,
    timezone: string
): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-GB', {
        // Pinned on purpose: these values are parsed back into numbers, so the
        // digits have to be Latin whatever the user's language is.
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: timezone
    }).formatToParts(date);

    return {
        year: Number(parts.find((p) => p.type === 'year')!.value),
        month: Number(parts.find((p) => p.type === 'month')!.value),
        day: Number(parts.find((p) => p.type === 'day')!.value)
    };
}

export function formatSmartShortTimeAndDate(
    date: Date,
    opts: FormatOptions
): SmartShortTimeAndDate {
    const now = new Date();
    const inputParts = getDatePartsInTimezone(date, opts.timezone);
    const nowParts = getDatePartsInTimezone(now, opts.timezone);

    const time = formatTime(date, opts);

    const isSameDay =
        inputParts.year === nowParts.year &&
        inputParts.month === nowParts.month &&
        inputParts.day === nowParts.day;

    if (isSameDay) {
        return { kind: 'today', time };
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayParts = getDatePartsInTimezone(yesterday, opts.timezone);
    const isYesterday =
        inputParts.year === yesterdayParts.year &&
        inputParts.month === yesterdayParts.month &&
        inputParts.day === yesterdayParts.day;

    if (isYesterday) {
        return { kind: 'yesterday', time };
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: 'short',
        timeZone: opts.timezone
    };
    if (inputParts.year !== nowParts.year) {
        dateOptions.year = 'numeric';
    }

    const dateStr = new Intl.DateTimeFormat(opts.locale, dateOptions).format(
        date
    );
    return { kind: 'date', time, date: dateStr };
}

export function formatDateRange(
    from: Date,
    to: Date,
    opts: FormatOptions
): string {
    const fromParts = getDatePartsInTimezone(from, opts.timezone);
    const toParts = getDatePartsInTimezone(to, opts.timezone);
    const nowParts = getDatePartsInTimezone(new Date(), opts.timezone);

    const fromOptions: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        timeZone: opts.timezone
    };
    const toOptions: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        timeZone: opts.timezone
    };

    if (fromParts.year !== nowParts.year) {
        fromOptions.year = 'numeric';
    }
    if (toParts.year !== nowParts.year) {
        toOptions.year = 'numeric';
    }

    const fromStr = new Intl.DateTimeFormat(opts.locale, fromOptions).format(
        from
    );
    const toStr = new Intl.DateTimeFormat(opts.locale, toOptions).format(to);

    if (
        fromParts.year === toParts.year &&
        fromParts.month === toParts.month &&
        fromParts.day === toParts.day
    ) {
        return fromStr;
    }

    return `${fromStr} - ${toStr}`;
}
